import { RootState } from "../../../redux-store";
import { createSelector } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import { selectAllCargoViews } from "../cargo-views/selectors";

export const selectRouteIds = (state: RootState) =>
  state.data.routeViewModels.routeIds;

export const selectRouteViewModel = (id: string) => (rootState: RootState) =>
  rootState.data.routeViewModels.entities[id];

// Preserves the previous behavior exactly: one badge for the single
// most-recently-logged-in driver per route, not one per driver.
export const selectMostRecentDriverLogin = (routeId: string) =>
  createSelector(selectRouteViewModel(routeId), (route) => {
    const entries = Object.entries(route?.driverLogins ?? {});
    if (!entries.length) {
      return null;
    }
    const [driverId, login] = entries.reduce((a, b) =>
      a[1].loggedInAt > b[1].loggedInAt ? a : b
    );
    return {
      driverId,
      truckId: login.truckId,
      trailerId: login.trailerId,
      loggedInAt: DateTime.fromISO(login.loggedInAt),
    };
  });

export const selectRouteForCargo =
  (cargoId: string) => (rootState: RootState) =>
    Object.values(rootState.data.routeViewModels.entities).find(
      (route) => route && route.cargoOrder.includes(cargoId)
    )?.id || null;

// Mirrors the old selectRouteStopStatus/selectCompletedStopFromActivities
// pair from the driver-activities duck, now sourced from the route view's
// stopStatuses (denormalized from stops.arrived_date/departed_date/
// completed_date) instead of the activity log.
export const selectStopStatus = (cargoId: string, stopId: string) =>
  createSelector(
    selectRouteForCargo(cargoId),
    (rootState: RootState) => rootState.data.routeViewModels.entities,
    (routeId, entities) => {
      const stopStatus = routeId ? entities[routeId]?.stopStatuses[stopId] : undefined;
      if (!stopStatus) {
        return { status: null, completedOrDeparted: null } as const;
      }
      const status = stopStatus.completedDate || stopStatus.departedDate
        ? ("complete" as const)
        : stopStatus.arrivedDate
          ? ("in-progress" as const)
          : null;
      const completedOrDeparted =
        stopStatus.completedDate && stopStatus.completedByDriverId
          ? {
              date: DateTime.fromISO(stopStatus.completedDate),
              driverId: stopStatus.completedByDriverId,
            }
          : stopStatus.departedDate && stopStatus.departedByDriverId
            ? {
                date: DateTime.fromISO(stopStatus.departedDate),
                driverId: stopStatus.departedByDriverId,
              }
            : null;
      return { status, completedOrDeparted };
    }
  );

export const selectOperationInProgress =
  (routeId: string) => (rootState: RootState) =>
    rootState.data.routeViewModels.entities[routeId]?.operationInProgress ||
    false;

export const selectRouteHasSplittableCargos = (routeId: string) =>
  createSelector(
    selectRouteViewModel(routeId),
    selectAllCargoViews,
    (routeViewModel, cargoViews) =>
      (routeViewModel?.cargoOrder.some(
        (cargoId) => !cargoViews[cargoId]?.hasBeenSplit
      ) &&
        !!routeViewModel?.lastStop) ||
      false
  );
